<template>
  <div>
    <v-btn
      class="ml-2"
      small
      :color="item === null ? 'green' : 'primary'"
      dark
      @click="openDialog"
    >
      <v-icon small left>mdi-plus</v-icon>
      {{ item === null ? 'Add' : 'Update' }} Notification Hook
    </v-btn>

    <v-dialog v-model="dialog" width="900" persistent scrollable>
      <v-card>
        <v-card-title class="pb-2">
          <v-icon left color="primary">mdi-bell-cog</v-icon>
          {{ item === null ? 'Add' : 'Manage' }} Notification Hook
        </v-card-title>

        <v-divider />

        <v-card-text style="max-height: 75vh; overflow-y: auto; padding-top: 16px;">
          <!-- Top fields: Template, Service, Method -->
          <v-row dense>
            <v-col cols="12">
              <feathers-vuex-find
                service="bh-notification-templates"
                :query="{ $limit: 100 }"
                :fetch-query="{ $limit: 100 }"
              >
                <v-combobox
                  slot-scope="{ items }"
                  v-model="form.nt_id"
                  label="Target Template"
                  :items="parseTemplates(items)"
                  item-text="text"
                  item-value="id"
                  :disabled="loading"
                  outlined
                  dense
                />
              </feathers-vuex-find>
            </v-col>
            <v-col cols="8">
              <feathers-vuex-find service="service-lists" :query="{}">
                <v-combobox
                  slot-scope="{ items }"
                  v-model="form.service"
                  label="Target Service"
                  :items="servicesFilter(items)"
                  :disabled="loading"
                  outlined
                  dense
                />
              </feathers-vuex-find>
            </v-col>
            <v-col cols="4">
              <v-combobox
                v-model="form.method"
                label="Method"
                :items="methodOptions"
                item-text="text"
                item-value="id"
                :disabled="loading"
                outlined
                dense
              />
            </v-col>
          </v-row>

          <v-divider class="mb-4" />

          <!-- Data Replacement Visual Editor -->
          <div class="editor-section-label mb-2">
            <v-icon small left color="primary">mdi-code-json</v-icon>
            <strong>Data Replacement</strong>
            <span class="ml-2 grey--text text--darken-1" style="font-size:12px;">
              Build the payload that maps template placeholders to dynamic values
            </span>
          </div>

          <data-replacement-editor
            v-model="form.data"
          />
        </v-card-text>

        <v-divider />

        <v-card-actions>
          <v-spacer />
          <v-btn v-if="!loading" small color="red" text @click="dialog = false">Cancel</v-btn>
          <v-btn small color="primary" text @click="UpdateNotificationHook" :loading="loading">Submit</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import DataReplacementEditor from './DataReplacementEditor.vue';

export default {
  components: { DataReplacementEditor },

  props: {
    item: {
      type: Object,
      default() {
        return {
          nt_id: null,
          service: null,
          method: null,
          enable_record: null,
          data: null,
          transformer: null
        };
      }
    }
  },

  data() {
    return {
      dialog: false,
      loading: false,
      form: {
        nt_id: null,
        service: null,
        method: null,
        enable_record: null,
        data: null,
        transformer: null
      }
    };
  },

  computed: {
    methodOptions() {
      return [
        { id: 'CREATE', text: 'Create' },
        { id: 'UPDATE', text: 'Update' }
      ];
    },
    ...mapGetters('bh-notification-templates', { getNotificationTemplate: 'get' })
  },

  methods: {
    openDialog() {
      this.form = {
        nt_id: this.item ? this.item.nt_id : null,
        service: this.item ? this.item.service : null,
        method: this.item ? this.item.method : null,
        enable_record: this.item ? this.item.enable_record : null,
        data: this.item ? this.item.data : null,
        transformer: this.item ? this.item.transformer : null
      };
      this.dialog = true;
    },

    async UpdateNotificationHook() {
      try {
        this.loading = true;
        const ntId = typeof this.form.nt_id === 'object' && this.form.nt_id
          ? this.form.nt_id.id
          : this.form.nt_id || 0;
        const method = typeof this.form.method === 'object' && this.form.method
          ? this.form.method.id
          : this.form.method;

        if (this.item && this.item.id) {
          this.item.nt_id = ntId;
          this.item.service = this.form.service;
          this.item.method = method;
          this.item.enable_record = !(!this.form.enable_record);
          this.item.data = this.form.data;
          this.item.transformer = this.form.transformer;
          await this.item.patch();
        } else {
          await this.createNewHook({
            ...this.form,
            nt_id: ntId,
            method,
            enable_record: !(!this.form.enable_record)
          });
          this.form = { nt_id: null, service: null, method: null, enable_record: null, data: null, transformer: null };
        }
        this.$emit('success');
        this.dialog = false;
      } catch (err) {
        console.log(err);
        this.$emit('error', err);
      } finally {
        this.loading = false;
      }
    },

    servicesFilter(items) {
      return [
        'authentication',
        ...(items.map(i => i.id).filter(service => [
          'logic-control',
          'cities',
          'countries',
          'regions',
          'logic-control-category',
          'bh-app-error-log',
          'bh-app-version',
          'bh-exp-tables',
          'access-control-services',
          'bh-user-obtained-exp',
          'bh-exp-hooks'
        ].indexOf(service) === -1))
      ];
    },

    parseTemplates(items) {
      return items.map(item => ({
        id: item.id,
        text: `[${item.id}] ${item.target_type} — ${item.name || ''}`
      }));
    },

    ...mapActions('bh-notification-hooks', { createNewHook: 'create' })
  }
};
</script>

<style scoped>
.editor-section-label {
  display: flex;
  align-items: center;
}
</style>
<!-- <script lang="ts">
import Vue from 'vue';
import { mapActions, mapGetters } from 'vuex';
import { Component, Prop } from 'vue-property-decorator';

@Component({
    computed: {
        ...mapGetters('bh-notification-templates', { getNotificationTemplate: 'get' })
    },
    methods: {
        ...mapActions('bh-notification-hooks', { createNewHook: 'create' }),
    }
})
export default class AddNotificationHook extends Vue {
    createNewHook: any;
    getNotificationTemplate: any;

    // Props
    @Prop({ default() { return {
        nt_id: null,
        service: null,
        method: null,
        enable_record: null,
        data: null,
        transformer: null
    }; } })
    item!: any;

    // Data
    dialog = false;
    loading = false;
    form = {
        nt_id: this.item.nt_id,
        service: this.item.service,
        method: this.item.method,
        enable_record: this.item.enable_record,
        data: this.item.data,
        transformer: this.item.transformer
    };

    // Methods
    public async UpdateNotificationHook() {
        try {
            this.loading = true;
            if(this.item) {
                this.item.nt_id = this.form.nt_id ? this.form.nt_id.id : 0;
                this.item.service = this.form.service;
                this.item.method = this.form.method.id;
                this.item.enable_record = !(!this.form.enable_record);
                this.item.data = this.form.data;
                this.item.transformer = this.form.transformer;
                await this.item.patch();
            } else {
                await this.createNewHook({
                    ...this.form,
                    nt_id: this.form.nt_id ? this.form.nt_id.id : 0,
                    method: this.form.method.id,
                    enable_record: !(!this.form.enable_record)
                });
                this.form.nt_id = null;
                this.form.service = null;
                this.form.method = null;
                this.form.enable_record = null
                this.form.data = null;
                this.form.transformer = null;
            }
            this.$emit('success');
            this.dialog = false;
        } catch(err) {
            console.log(err);
            this.$emit('error', err);
        } finally {
            this.loading = false;
        }
    }

    public servicesFilter(items: any[]) {
        return [
            'authentication',
            ...items.map(i => i.id).filter(service => [
                'logic-control',
                'cities',
                'countries',
                'regions',
                'logic-control-category',
                'bh-app-error-log',
                'bh-app-version',
                'bh-exp-tables',
                'access-control-services',
                'bh-user-obtained-exp',
                'bh-exp-hooks'
            ].indexOf(service) === -1)
        ];
    }

    public get methodOptions() {
        return [
            { id: 'CREATE', text: 'Create' },
            { id: 'UPDATE', text: 'Update' }
        ];
    }

    public parseTemplates(items: any[]) {
        console.log(items);
        return items.map(item => {
            return {
                id: item.id,
                text: item.target_type
            };
        });
    }

}
</script> -->
